/**
 * Middleware de Segurança - Melhores práticas do mercado
 * 
 * Implementa:
 * - Session timeout automático (30 min)
 * - Rate limiting (backend + frontend)
 * - Headers de segurança
 * - Input validation (XSS, SQL injection)
 * - Audit logging
 */

import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";

// Configuração de timeout de sessão (30 minutos de inatividade)
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutos
let lastActivity = Date.now();
let timeoutTimer: NodeJS.Timeout | null = null;

/**
 * Atualiza o timestamp de última atividade
 */
export const updateActivity = () => {
  lastActivity = Date.now();
  
  // Reseta o timer de timeout
  if (timeoutTimer) {
    clearTimeout(timeoutTimer);
  }
  
  // Configura novo timer para fazer logout automático
  timeoutTimer = setTimeout(async () => {
    const timeSinceLastActivity = Date.now() - lastActivity;
    
    if (timeSinceLastActivity >= SESSION_TIMEOUT) {
      await supabase.auth.signOut();
      window.location.href = '/auth';
    }
  }, SESSION_TIMEOUT);
};

/**
 * Inicializa monitoramento de atividade do usuário
 */
export const initActivityMonitoring = () => {
  // Monitora atividade do usuário
  const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
  
  events.forEach(event => {
    document.addEventListener(event, updateActivity, { passive: true });
  });
  
  updateActivity();
};

/**
 * Rate Limiting local para prevenir spam de requisições
 */
class RateLimiter {
  private attempts: Map<string, number[]> = new Map();
  private maxAttempts: number;
  private windowMs: number;

  constructor(maxAttempts: number = 5, windowMs: number = 60000) {
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
  }

  /**
   * Verifica se a ação está dentro do limite
   */
  checkLimit(key: string): boolean {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    
    // Remove tentativas antigas
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    
    if (recentAttempts.length >= this.maxAttempts) {
      return false; // Bloqueado
    }
    
    // Adiciona nova tentativa
    recentAttempts.push(now);
    this.attempts.set(key, recentAttempts);
    
    return true; // Permitido
  }

  /**
   * Reseta o contador para uma chave específica
   */
  reset(key: string) {
    this.attempts.delete(key);
  }
}

// Instância global de rate limiter
export const rateLimiter = new RateLimiter(5, 60000); // 5 tentativas por minuto

/**
 * Audit Log Helper - Registra ações importantes para auditoria
 */
export const logAuditEvent = async (
  action: string,
  tableName?: string,
  recordId?: string,
  oldData?: Record<string, any>,
  newData?: Record<string, any>
) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    await supabase.from('audit_logs').insert({
      user_id: user?.id || null,
      action,
      table_name: tableName,
      record_id: recordId,
      old_data: oldData,
      new_data: newData,
      user_agent: navigator.userAgent,
    });
  } catch (error) {
    console.error('Failed to log audit event:', error);
  }
};

/**
 * Headers de segurança recomendados
 */
export const securityHeaders = {
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'DENY',
  'X-XSS-Protection': '1; mode=block',
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  'Permissions-Policy': 'geolocation=(), microphone=(), camera=()',
};

/**
 * Sanitiza input do usuário para prevenir XSS
 */
export const sanitizeInput = (input: string): string => {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };
  const reg = /[&<>"'/]/gi;
  return input.replace(reg, (match) => map[match]);
};

/**
 * Schema de validação para autenticação usando Zod
 */
export const authSchemas = {
  email: z.string()
    .trim()
    .email({ message: "Email inválido" })
    .max(255, { message: "Email muito longo" }),
  
  password: z.string()
    .min(8, { message: "Senha deve ter no mínimo 8 caracteres" })
    .max(72, { message: "Senha deve ter no máximo 72 caracteres" })
    .regex(/[A-Z]/, { message: "Deve conter letra maiúscula" })
    .regex(/[a-z]/, { message: "Deve conter letra minúscula" })
    .regex(/[0-9]/, { message: "Deve conter um número" })
    .regex(/[^A-Za-z0-9]/, { message: "Deve conter caractere especial" }),
  
  fullName: z.string()
    .trim()
    .min(2, { message: "Nome deve ter no mínimo 2 caracteres" })
    .max(100, { message: "Nome muito longo" })
    .regex(/^[\p{L}\s'-]+$/u, { message: "Nome inválido" }),
};

/**
 * Valida força da senha (mantido para compatibilidade)
 */
export const validatePasswordStrength = (password: string): {
  isValid: boolean;
  errors: string[];
} => {
  const result = authSchemas.password.safeParse(password);
  
  if (result.success) {
    return { isValid: true, errors: [] };
  }
  
  return {
    isValid: false,
    errors: result.error.errors.map(e => e.message)
  };
};

/**
 * Detecta e bloqueia tentativas de SQL Injection
 */
export const detectSQLInjection = (input: string): boolean => {
  const sqlPatterns = [
    /(\bSELECT\b|\bINSERT\b|\bUPDATE\b|\bDELETE\b|\bDROP\b|\bCREATE\b|\bALTER\b)/i,
    /(\bUNION\b|\bJOIN\b)/i,
    /(--|;|\/\*|\*\/)/,
    /(\bOR\b|\bAND\b)\s+\d+\s*=\s*\d+/i,
  ];
  
  return sqlPatterns.some(pattern => pattern.test(input));
};
